const functionalAssignmentSymbols = ['==', '≠'] as const;
type FunctionalAssignmentSymbol = typeof functionalAssignmentSymbols[number];

export class SymbolWithText<TSymbol extends string> {
  constructor(
    private readonly defaultSymbol: TSymbol,
    public readonly symbol: TSymbol | '' = '',
    public readonly text: string = '',
  ) {
  }

  public setText(text: string) {
    return new SymbolWithText(
      this.defaultSymbol,
      text ? (this.symbol || this.defaultSymbol) : '',
      text,
    );
  }
}

export function isBmkStructure(value: unknown): value is BmkStructure {
  return value instanceof BmkStructure;
}

export class BmkStructure {
  constructor(
    public readonly userDefined: string = '',
    public readonly functionalAssignment = new SymbolWithText<'==' | '≠'>('=='),
    public readonly functionDesignation = new SymbolWithText<'='>('='),
    public readonly installationSite = new SymbolWithText<'++'>('++'),
    public readonly locationDesignation = new SymbolWithText<'+'>('+'),
    public readonly deviceTag = new SymbolWithText<'-'>('-'),
  ) {
  }

  public with(args: Partial<Omit<BmkStructure, 'userDefined'>>): BmkStructure {
    return new BmkStructure(
      this.userDefined,
      args.functionalAssignment ?? this.functionalAssignment,
      args.functionDesignation ?? this.functionDesignation,
      args.installationSite ?? this.installationSite,
      args.locationDesignation ?? this.locationDesignation,
      args.deviceTag ?? this.deviceTag,
    );
  }

  public toString(): string {
    let bmkString = this.userDefined;
    if (this.functionalAssignment.symbol) {
      bmkString += this.functionalAssignment.symbol + this.functionalAssignment.text;
    }
    if (this.functionDesignation.symbol) {
      bmkString += this.functionDesignation.symbol + this.functionDesignation.text;
    }
    if (this.installationSite.symbol) {
      bmkString += this.installationSite.symbol + this.installationSite.text;
    }
    if (this.locationDesignation.symbol) {
      bmkString += this.locationDesignation.symbol + this.locationDesignation.text;
    }
    if (this.deviceTag.symbol) {
      bmkString += this.deviceTag.symbol + this.deviceTag.text;
    }
    return bmkString;
  }

  public static getCommonPartsFromBmkStrings(bmkStrings: string[]): BmkStructure {
    let functionalAssignmentText = '';
    let functionDesignationText = '';
    let installationSiteText = '';
    let locationDesignationText = '';
    let deviceTagText = '';

    let functionalAssignmentSymbol = '';
    let functionDesignationSymbol = '';
    let installationSiteSymbol = '';
    let locationDesignationSymbol = '';
    let deviceTagSymbol = '';

    let functionalAssignmentIsCommon = true;
    let functionDesignationIsCommon = true;
    let installationSiteIsCommon = true;
    let locationDesignationIsCommon = true;
    let deviceTagIsCommon = true;

    for (const bmkString of bmkStrings) {
      const bmkStruct = this.parseBmkString(bmkString);
      if (bmkStruct instanceof BmkStructure) {
        functionalAssignmentText = functionalAssignmentText || bmkStruct.functionalAssignment.text;
        functionalAssignmentSymbol = functionalAssignmentSymbol || bmkStruct.functionalAssignment.symbol;
        if (functionalAssignmentText !== bmkStruct.functionalAssignment.text) {
          functionalAssignmentIsCommon = false;
        }
        functionDesignationText = functionDesignationText || bmkStruct.functionDesignation.text;
        functionDesignationSymbol = functionDesignationSymbol || bmkStruct.functionDesignation.symbol;
        if (functionDesignationText !== bmkStruct.functionDesignation.text) {
          functionDesignationIsCommon = false;
        }
        installationSiteText = installationSiteText || bmkStruct.installationSite.text;
        installationSiteSymbol = installationSiteSymbol || bmkStruct.installationSite.symbol;
        if (installationSiteText !== bmkStruct.installationSite.text) {
          installationSiteIsCommon = false;
        }
        locationDesignationText = locationDesignationText || bmkStruct.locationDesignation.text;
        locationDesignationSymbol = locationDesignationSymbol || bmkStruct.locationDesignation.symbol;
        if (locationDesignationText !== bmkStruct.locationDesignation.text) {
          locationDesignationIsCommon = false;
        }
        deviceTagText = deviceTagText || bmkStruct.deviceTag.text;
        deviceTagSymbol = deviceTagSymbol || bmkStruct.deviceTag.symbol;
        if (deviceTagText !== bmkStruct.deviceTag.text) {
          deviceTagIsCommon = false;
        }
      }
    }

    return new BmkStructure(
      "",
      functionalAssignmentIsCommon ? new SymbolWithText<'==' | '≠'>('==', functionalAssignmentSymbol as FunctionalAssignmentSymbol, functionalAssignmentText) : undefined,
      functionDesignationIsCommon ? new SymbolWithText<'='>('=', functionDesignationSymbol as '=', functionDesignationText) : undefined,
      installationSiteIsCommon ? new SymbolWithText<'++'>('++', installationSiteSymbol as '++', installationSiteText) : undefined,
      locationDesignationIsCommon ? new SymbolWithText<'+'>('+', locationDesignationSymbol as '+', locationDesignationText) : undefined,
      deviceTagIsCommon ? new SymbolWithText<'-'>('-', deviceTagSymbol as '-', deviceTagText) : undefined
    );
  }

  public static parseBmkString(bmkString: string): BmkStructure | string {

    if (!/^[^≠=+\-]{0,32}((==|≠)[^≠=+\-]{0,32})?((=)[^≠=+\-]{0,32})?((\+\+)[^≠=+\-]{0,32})?((\+)[^≠=+\-]{0,32})?((-)[^≠=+\-]{0,32})?$/.test(bmkString)) {
      return bmkString;
    }

    const functionalAssignment = this.findSymbol(bmkString, functionalAssignmentSymbols, 0);
    const functionalDesignation = this.findSymbol(bmkString, ['='], functionalAssignment.position);
    const installationSite = this.findSymbol(bmkString, ['++'], functionalDesignation.position);
    const locationDesignation = this.findSymbol(bmkString, ['+'], installationSite.position);
    const deviceTag = this.findSymbol(bmkString, ['-'], locationDesignation.position);

    const deviceTagText = this.getText(bmkString, deviceTag.symbol, deviceTag.position, bmkString.length);
    const locationDesignationText = this.getText(bmkString, locationDesignation.symbol, locationDesignation.position, deviceTagText.position);
    const installationSiteText = this.getText(bmkString, installationSite.symbol, installationSite.position, locationDesignationText.position);
    const functionalDesignationText = this.getText(bmkString, functionalDesignation.symbol, functionalDesignation.position, installationSiteText.position);
    const functionalAssignmentText = this.getText(bmkString, functionalAssignment.symbol, functionalAssignment.position, functionalDesignationText.position);
    const userDefinedText = this.getText(bmkString, '', 0, functionalAssignmentText.position);

    return new BmkStructure(
      userDefinedText.text,
      new SymbolWithText<'==' | '≠'>('==', functionalAssignment.symbol as FunctionalAssignmentSymbol, functionalAssignmentText.text),
      new SymbolWithText<'='>('=', functionalDesignation.symbol as '=', functionalDesignationText.text),
      new SymbolWithText<'++'>('++', installationSite.symbol as '++', installationSiteText.text),
      new SymbolWithText<'+'>('+', locationDesignation.symbol as '+', locationDesignationText.text),
      new SymbolWithText<'-'>('-', deviceTag.symbol as '-', deviceTagText.text),
    );
  }

  private static findSymbol(bmkString: string, symbols: readonly string[], position: number): { symbol: string | undefined, position: number } {
    for (const symbol of symbols) {
      const index = bmkString.indexOf(symbol, position);
      if (index !== -1) {
        return {symbol, position: index + symbol.length};
      }
    }
    return {symbol: undefined, position};
  }

  private static getText(bmkString: string, symbol: string | undefined, startPosition: number, position: number): { text: string, position: number } {
    if (symbol === undefined) {
      return {text: '', position};
    }
    return {text: bmkString.substring(startPosition, position), position: startPosition - symbol.length};
  }
}
